import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useAISummaryAvailability } from '../../contexts/AISummaryContext';
import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Divider,
  Collapse,
  ListItemButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import LocationCityOutlinedIcon from '@mui/icons-material/LocationCityOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import AutoGraphOutlinedIcon from '@mui/icons-material/AutoGraphOutlined';
import DonutSmallOutlinedIcon from '@mui/icons-material/DonutSmallOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import LockResetIcon from '@mui/icons-material/LockReset';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BuildIcon from '@mui/icons-material/Build';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import BugReportIcon from '@mui/icons-material/BugReport';
import ShareIcon from '@mui/icons-material/Share';
import ContactSupportOutlinedIcon from '@mui/icons-material/ContactSupportOutlined';
import PublicIcon from '@mui/icons-material/Public';
import PushPinIcon from '@mui/icons-material/PushPin';
import PersonPinCircleOutlinedIcon from '@mui/icons-material/PersonPinCircleOutlined';
import AssistantOutlinedIcon from '@mui/icons-material/AssistantOutlined';
import AssistantIcon from '@mui/icons-material/Assistant';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SchoolIcon from '@mui/icons-material/School';
import LoginIcon from '@mui/icons-material/Login';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import EngineeringOutlinedIcon from '@mui/icons-material/EngineeringOutlined';

import { ROLES } from '../../constants/roleAccess';
import { PRODUCTS, hasProductAccess, setActiveProduct } from '../../constants/productAccess';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

const SIDEBAR_NAV_STORAGE_KEY = 'edc_sidebar_nav_expanded_v1';

/** @typedef {{ text: string, icon: React.ReactNode, path: string, roles?: string[] }} MenuItemDef */

/** @typedef {{ id: string, label: string, items: MenuItemDef[] }} AdminSectionDef */

/** Main nav: flat list in original order (no sections). */
const MAIN_NAV_ITEMS = [
  { text: 'Portfolio Analysis', icon: <MapOutlinedIcon />, path: '/district-analysis' },
  { text: 'School Summary', icon: <AssessmentOutlinedIcon />, path: '/school-summary' },
  {
    text: 'Geographic Explorer',
    icon: <MapsHomeWorkOutlinedIcon />,
    path: '/geographic-explorer',
    roles: [ROLES.ADMIN, ROLES.UPDATED_WRITE, ROLES.UPDATED_READ, ROLES.PUBLIC],
  },
  {
    text: 'Competitive Explorer',
    icon: <DonutSmallOutlinedIcon />,
    path: '/competitive-explorer',
    roles: [ROLES.ADMIN, ROLES.UPDATED_WRITE, ROLES.UPDATED_READ, ROLES.PUBLIC],
  },
  {
    text: 'Projections Explorer',
    icon: <AutoGraphOutlinedIcon />,
    path: '/projections-explorer',
    roles: [ROLES.ADMIN, ROLES.UPDATED_WRITE, ROLES.UPDATED_READ, ROLES.PUBLIC],
  },
  {
    text: 'Student Catchment Explorer',
    icon: <PersonPinCircleOutlinedIcon />,
    path: '/student-catchment-explorer',
    roles: [ROLES.ADMIN, ROLES.UPDATED_WRITE, ROLES.UPDATED_READ, ROLES.PUBLIC],
  },
  {
    text: 'AI Summary',
    icon: <AssistantOutlinedIcon />,
    path: '/ai-summary',
    roles: [ROLES.ADMIN, ROLES.UPDATED_WRITE, ROLES.UPDATED_READ, ROLES.PUBLIC],
  },
  { text: 'Resources', icon: <ShareIcon />, path: '/resources' },
  { text: 'Support', icon: <ContactSupportOutlinedIcon />, path: '/support' },
];

/** Nav shown instead of MAIN_NAV_ITEMS/ADMIN_SECTIONS while viewing City Explorer. */
const CITY_EXPLORER_NAV_ITEMS = [
  { text: 'Locations', icon: <LocationCityOutlinedIcon />, path: '/city-explorer' },
];

/** Admin-only sections (collapsible); shown after main nav + divider. */
const ADMIN_SECTIONS = [
  {
    id: 'admin-operations',
    label: 'Admin · Operations',
    items: [
      { text: 'User & Team Management', icon: <ManageAccountsIcon />, path: '/admin/user-management', roles: [ROLES.ADMIN] },
      { text: 'Public Links', icon: <PublicIcon />, path: '/public-links', roles: [ROLES.ADMIN] },
      { text: 'School Processor', icon: <BuildIcon />, path: '/admin/school-processor', roles: [ROLES.ADMIN] },
      { text: 'Schools Validation', icon: <PlaylistAddCheckIcon />, path: '/schools-validation', roles: [ROLES.ADMIN] },
      { text: 'Academic Data', icon: <SchoolIcon />, path: '/admin/academic-data', roles: [ROLES.ADMIN] },
      { text: 'Drop-a-Pin Locations', icon: <PushPinIcon />, path: '/admin/drop-a-pin', roles: [ROLES.ADMIN] },
      { text: 'AI School Summary', icon: <AssistantIcon />, path: '/admin/ai-summary', roles: [ROLES.ADMIN] },
    ],
  },
  {
    id: 'admin-insights',
    label: 'Admin · Insights',
    items: [
      { text: 'Analytics', icon: <AnalyticsIcon />, path: '/admin/analytics', roles: [ROLES.ADMIN] },
      { text: 'Login Analytics', icon: <LoginIcon />, path: '/admin/login-analytics', roles: [ROLES.ADMIN] },
      { text: 'Product Usage', icon: <InsightsIcon />, path: '/admin/product-usage', roles: [ROLES.ADMIN] },
      { text: 'Feedback', icon: <FeedbackIcon />, path: '/admin/feedback', roles: [ROLES.ADMIN] },
      { text: 'TX District Tool', icon: <LocationCityOutlinedIcon />, path: '/admin/tx-district-tool', roles: [ROLES.ADMIN] },
      { text: 'Projections Analysis', icon: <ShowChartIcon />, path: '/admin/projections-analysis', roles: [ROLES.ADMIN] },
    ],
  },
  {
    id: 'admin-system',
    label: 'Admin · System',
    items: [{ text: 'Debug Tools', icon: <BugReportIcon />, path: '/admin/debug', roles: [ROLES.ADMIN] }],
  },
];

function adminSectionHeaderIcon(sectionId) {
  switch (sectionId) {
    case 'admin-operations':
      return <EngineeringOutlinedIcon fontSize="small" />;
    case 'admin-insights':
      return <InsightsOutlinedIcon fontSize="small" />;
    case 'admin-system':
      return <BugReportIcon fontSize="small" />;
    default:
      return null;
  }
}

/**
 * @param {MenuItemDef} item
 * @param {{ role?: string }} user
 * @param {boolean} aiLoading
 * @param {boolean} hasAISummaries
 * @param {boolean} isAdmin
 */
function itemVisibleForUser(item, user, aiLoading, hasAISummaries, isAdmin) {
  if (item.path === '/ai-summary') {
    if (aiLoading) return false;
    if (!item.roles || !item.roles.includes(user?.role)) return false;
    if (isAdmin) return true;
    return hasAISummaries;
  }
  if (!item.roles) return true;
  return item.roles.includes(user?.role);
}

function buildDefaultExpanded(pathname, adminSections) {
  /** @type {Record<string, boolean>} */
  const next = {};
  adminSections.forEach((sec) => {
    next[sec.id] = sec.items.some((i) => i.path === pathname);
  });
  return next;
}

function loadStoredExpanded() {
  try {
    const raw = localStorage.getItem(SIDEBAR_NAV_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch {
    /* ignore */
  }
  return null;
}

function saveExpanded(expanded) {
  try {
    localStorage.setItem(SIDEBAR_NAV_STORAGE_KEY, JSON.stringify(expanded));
  } catch {
    /* ignore */
  }
}

const Sidebar = ({ user, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [navSearch, setNavSearch] = useState('');
  const { logout } = useAuth();
  const { hasAISummaries, isAdmin, loading: aiLoading } = useAISummaryAvailability();

  // City Explorer is a separate product with its own minimal nav - it
  // doesn't show School Explorer's tools or admin sections. Determined by
  // route rather than session state so a direct link/refresh still works.
  const isCityExplorer = location.pathname.startsWith('/city-explorer');
  const canSwitchToSchoolExplorer = isCityExplorer && hasProductAccess(user, PRODUCTS.SCHOOL_EXPLORER);
  const canSwitchToCityExplorer = !isCityExplorer && hasProductAccess(user, PRODUCTS.CITY_EXPLORER);

  const initialExpanded = useMemo(() => {
    const s = loadStoredExpanded();
    return s && Object.keys(s).length > 0 ? { ...s } : {};
  }, []);
  const [expandedBySection, setExpandedBySection] = useState(initialExpanded);
  const defaultsAppliedRef = useRef(Object.keys(initialExpanded).length > 0);

  const visibleMainItems = useMemo(() => {
    if (isCityExplorer) return CITY_EXPLORER_NAV_ITEMS;
    return MAIN_NAV_ITEMS.filter((item) =>
      itemVisibleForUser(item, user, aiLoading, hasAISummaries, isAdmin)
    );
  }, [user, aiLoading, hasAISummaries, isAdmin, isCityExplorer]);

  const visibleAdminSections = useMemo(() => {
    if (isCityExplorer) return [];
    if (user?.role !== ROLES.ADMIN) return [];
    return ADMIN_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter((item) => itemVisibleForUser(item, user, aiLoading, hasAISummaries, isAdmin)),
    })).filter((sec) => sec.items.length > 0);
  }, [user, aiLoading, hasAISummaries, isAdmin, isCityExplorer]);

  const pathname = location.pathname;
  const searchQ = navSearch.trim().toLowerCase();

  const filteredAdminSections = useMemo(() => {
    if (!searchQ) return visibleAdminSections;
    return visibleAdminSections
      .map((sec) => {
        const labelMatch = sec.label.toLowerCase().includes(searchQ);
        const items = sec.items.filter(
          (item) => labelMatch || item.text.toLowerCase().includes(searchQ)
        );
        return { ...sec, items };
      })
      .filter((sec) => sec.items.length > 0);
  }, [visibleAdminSections, searchQ]);

  useEffect(() => {
    if (visibleAdminSections.length === 0) return;
    if (defaultsAppliedRef.current) {
      const containing = visibleAdminSections.find((sec) => sec.items.some((i) => i.path === pathname));
      if (!containing) return;
      setExpandedBySection((prev) => {
        if (prev[containing.id]) return prev;
        const next = { ...prev, [containing.id]: true };
        saveExpanded(next);
        return next;
      });
      return;
    }
    defaultsAppliedRef.current = true;
    const base = buildDefaultExpanded(pathname, visibleAdminSections);
    const containing = visibleAdminSections.find((sec) => sec.items.some((i) => i.path === pathname));
    if (containing) base[containing.id] = true;
    setExpandedBySection(base);
    saveExpanded(base);
  }, [pathname, visibleAdminSections]);

  const toggleSection = useCallback((sectionId) => {
    setExpandedBySection((prev) => {
      const next = { ...prev, [sectionId]: !prev[sectionId] };
      saveExpanded(next);
      return next;
    });
  }, []);

  const isSectionOpen = useCallback(
    (sectionId) => {
      if (searchQ) return true;
      return Boolean(expandedBySection[sectionId]);
    },
    [expandedBySection, searchQ]
  );

  const flatNavItems = useMemo(() => {
    const out = [...visibleMainItems];
    visibleAdminSections.forEach((sec) => {
      sec.items.forEach((item) => out.push(item));
    });
    return out;
  }, [visibleMainItems, visibleAdminSections]);

  const fullName = user?.name || user?.displayName || user?.email;

  const handleDrawerToggle = () => {
    setIsOpen(!isOpen);
    onToggle(!isOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const drawerWidth = isOpen ? 240 : 70;

  const renderNavRow = (item) => (
    <Tooltip key={item.path} title={!isOpen ? item.text : ''} placement="right">
      <ListItemButton
        component={RouterLink}
        to={item.path}
        selected={pathname === item.path}
        sx={{
          py: 1.25,
          minHeight: 44,
          pl: isOpen ? 3 : 1,
          pr: isOpen ? 2 : 1,
          justifyContent: isOpen ? 'flex-start' : 'center',
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: isOpen ? 2 : 0,
            justifyContent: 'center',
            color: pathname === item.path ? theme.palette.primary.main : theme.palette.text.secondary,
          }}
        >
          {item.icon}
        </ListItemIcon>
        {isOpen && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: '0.875rem',
              fontWeight: pathname === item.path ? 'bold' : 'normal',
              color: pathname === item.path ? theme.palette.primary.main : theme.palette.text.primary,
            }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: theme.palette.grey[200],
            color: theme.palette.text.primary,
            borderRight: 'none',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            boxShadow: '4px 0 10px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: isOpen ? 200 : 80,
              mb: 1,
              pt: isOpen ? 4 : 0,
              flexShrink: 0,
            }}
          >
            <Box
              component={RouterLink}
              to={isCityExplorer ? '/city-explorer' : '/dashboard'}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                '&:hover': { opacity: 0.8 },
              }}
            >
              <img
                src={isOpen ? '/images/edc_logo.png' : '/images/edc_logo_circle.png'}
                alt="EDC Logo"
                style={{
                  height: isOpen ? 175 : 50,
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
          </Box>

          {isOpen &&
            visibleAdminSections.length > 0 &&
            visibleAdminSections.reduce((n, s) => n + s.items.length, 0) > 8 && (
            <Box sx={{ px: 1.5, pb: 1, flexShrink: 0 }}>
              <TextField
                size="small"
                fullWidth
                placeholder="Filter admin tools…"
                value={navSearch}
                onChange={(e) => setNavSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.background.paper,
                    fontSize: '0.8125rem',
                  },
                }}
              />
            </Box>
          )}

          <List
            sx={{
              flexGrow: 1,
              mt: 2,
              overflowY: 'auto',
              overflowX: 'hidden',
              py: 0,
            }}
          >
            {!isOpen ? (
              flatNavItems.map((item) => renderNavRow(item))
            ) : (
              <>
                {visibleMainItems.map((item) => renderNavRow(item))}
                {visibleAdminSections.length > 0 && (
                  <>
                    <Divider sx={{ my: 1, mx: isOpen ? 2 : 1 }} />
                    {filteredAdminSections.map((section) => (
                      <React.Fragment key={section.id}>
                        <ListItemButton
                          onClick={() => toggleSection(section.id)}
                          sx={{
                            py: 0.75,
                            px: 2,
                            minHeight: 40,
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                            {adminSectionHeaderIcon(section.id)}
                          </ListItemIcon>
                          <ListItemText
                            primary={section.label}
                            primaryTypographyProps={{
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              letterSpacing: '0.02em',
                              color: 'text.secondary',
                              textTransform: 'uppercase',
                            }}
                          />
                          {searchQ ? null : isSectionOpen(section.id) ? (
                            <ExpandLess fontSize="small" />
                          ) : (
                            <ExpandMore fontSize="small" />
                          )}
                        </ListItemButton>
                        <Collapse in={isSectionOpen(section.id)} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {section.items.map((item) => (
                              <Box key={item.path} sx={{ pl: isOpen ? 1 : 0 }}>
                                {renderNavRow(item)}
                              </Box>
                            ))}
                          </List>
                        </Collapse>
                      </React.Fragment>
                    ))}
                  </>
                )}
              </>
            )}
          </List>

          {user && (
            <>
              <Divider sx={{ my: 1, flexShrink: 0 }} />
              <Box sx={{ p: isOpen ? 2 : 0, flexShrink: 0 }}>
                <Tooltip title={!isOpen ? fullName : ''} placement="right">
                  <ListItemButton
                    onClick={handleMenuOpen}
                    sx={{
                      borderRadius: 1,
                      justifyContent: 'center',
                      px: isOpen ? 2 : 0,
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isOpen ? 3 : 0,
                        justifyContent: 'center',
                      }}
                    >
                      <AccountCircleIcon color="primary" />
                    </ListItemIcon>
                    {isOpen && (
                      <ListItemText
                        primary={fullName}
                        primaryTypographyProps={{
                          fontSize: '0.9rem',
                          fontWeight: 'medium',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </Box>
            </>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, flexShrink: 0 }}>
            <Tooltip title={isOpen ? 'Close sidebar' : 'Open sidebar'} placement="right">
              <IconButton onClick={handleDrawerToggle}>
                {isOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Drawer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiMenuItem-root': {
              padding: '10px 15px',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
            },
          },
        }}
      >
        <MenuItem component={RouterLink} to="/preferences">
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Privacy Preferences</ListItemText>
        </MenuItem>
        <MenuItem component={RouterLink} to="/change-password">
          <ListItemIcon>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Change Password</ListItemText>
        </MenuItem>
        {(canSwitchToCityExplorer || canSwitchToSchoolExplorer) && (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (canSwitchToCityExplorer) {
                setActiveProduct(PRODUCTS.CITY_EXPLORER);
                navigate('/city-explorer');
              } else {
                setActiveProduct(PRODUCTS.SCHOOL_EXPLORER);
                navigate('/dashboard');
              }
            }}
          >
            <ListItemIcon>
              <SwapHorizIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              Switch to {canSwitchToCityExplorer ? 'City Explorer' : 'School Explorer'}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={async () => {
            handleMenuClose();
            await logout();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default Sidebar;
